import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './LiquidationSummaryModal.css';

function LiquidationSummaryModal({ show, onClose, summary: summaryProp }) {
  const { t } = useTranslation();
  const [transformedSummary, setTransformedSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animatedValues, setAnimatedValues] = useState({
    totalVolume: 0,
    largestLiquidation: 0,
    netFlow: 0
  });
  const [lastUpdateText, setLastUpdateText] = useState('');
  const [summaryTimestamp, setSummaryTimestamp] = useState(null);

  // Process summary when it changes
  useEffect(() => {
    if (!summaryProp) return;

    setLoading(true);

    // Transform summary structure for liquidations
    const transformed = {
      totalVolume: summaryProp.liquidations?.totalVolume || 0,
      liquidationCount: summaryProp.liquidations?.count || 0,
      largestLiquidation: summaryProp.liquidations?.largestTrade || null,
      longLiqVolume: summaryProp.liquidations?.longVolume || 0,
      shortLiqVolume: summaryProp.liquidations?.shortVolume || 0,
      netFlow: (summaryProp.liquidations?.shortVolume || 0) - (summaryProp.liquidations?.longVolume || 0)
    };

    setTransformedSummary(transformed);
    setSummaryTimestamp(summaryProp.timestamp ? new Date(summaryProp.timestamp) : new Date());
    animateNumbers(transformed);
    setLoading(false);
  }, [summaryProp]);

  // Update "last updated" text every second
  useEffect(() => {
    if (!summaryTimestamp) return;

    const updateLastUpdateText = () => {
      const now = new Date();
      const diffMs = now - summaryTimestamp;
      const diffSec = Math.floor(diffMs / 1000);

      if (diffSec < 60) {
        setLastUpdateText(t('time.secondsAgo', { count: diffSec }));
      } else if (diffSec < 3600) {
        const mins = Math.floor(diffSec / 60);
        setLastUpdateText(t('time.minutesAgo', { count: mins }));
      } else {
        const hours = Math.floor(diffSec / 3600);
        setLastUpdateText(t('time.hoursAgo', { count: hours }));
      }
    };

    updateLastUpdateText();
    const interval = setInterval(updateLastUpdateText, 1000);

    return () => clearInterval(interval);
  }, [summaryTimestamp, t]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  // Animate numbers counting up
  const animateNumbers = (data) => {
    const duration = 1500; // 1.5 seconds
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setAnimatedValues({
        totalVolume: Math.floor(data.totalVolume * easeOut),
        largestLiquidation: data.largestLiquidation ? Math.floor(data.largestLiquidation.dollarSize * easeOut) : 0,
        netFlow: Math.floor(data.netFlow * easeOut)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, interval);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) {
      return '0';
    }

    const isNegative = value < 0;
    const absValue = Math.abs(value);

    let formatted;
    if (absValue >= 1e9) {
      formatted = `${(absValue / 1e9).toFixed(2)}B`;
    } else if (absValue >= 1e6) {
      formatted = `${(absValue / 1e6).toFixed(1)}M`;
    } else if (absValue >= 1e3) {
      formatted = `${(absValue / 1e3).toFixed(0)}K`;
    } else {
      formatted = absValue.toFixed(0);
    }

    return isNegative ? `-${formatted}` : formatted;
  };

  if (!show) return null;

  return (
    <div className={`liquidation-summary-overlay ${show ? 'show' : ''}`} onClick={onClose}>
      <div className={`liquidation-summary-modal ${show ? 'show' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="liquidation-summary-close" onClick={onClose}>×</button>

        {/* Header */}
        <div className="liquidation-summary-header">
          <div className="liquidation-summary-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM13 7L13.001 13H16L12 17L8 13H11V7H13Z"/>
            </svg>
          </div>
          <div className="liquidation-summary-title-container">
            <h2 className="liquidation-summary-title">{t('liquidationSummary.title')}</h2>
            {lastUpdateText && (
              <div className="liquidation-summary-updated">{t('liquidationSummary.updated')} {lastUpdateText}</div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="liquidation-summary-loading">
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        ) : transformedSummary ? (
          <div className="liquidation-summary-content">
            {/* Total Volume Card */}
            <div className="summary-card card-pulse">
              <div className="summary-card-label">{t('liquidationSummary.totalLiquidations')}</div>
              <div className="summary-card-value">{formatCurrency(animatedValues.totalVolume)} USD</div>
              <div className="summary-card-sublabel">{transformedSummary.liquidationCount} {t('liquidationSummary.liquidations')}</div>
            </div>

            {/* Largest Liquidation Card */}
            <div className="summary-card card-slide-left">
              <div className="summary-card-label">{t('liquidationSummary.largestLiquidation')}</div>
              <div className="summary-card-value">
                {transformedSummary.largestLiquidation ? formatCurrency(animatedValues.largestLiquidation) : '0'} USD
              </div>
              {transformedSummary.largestLiquidation && (
                <div className="summary-card-sublabel">
                  {transformedSummary.largestLiquidation.symbol.replace('USDT', '').replace('USD', '')} •{' '}
                  <span className={transformedSummary.largestLiquidation.side === 'sell' ? 'text-danger' : 'text-success'}>
                    {transformedSummary.largestLiquidation.side === 'sell' ? t('whaleTrades.long') : t('whaleTrades.short')}
                  </span>
                </div>
              )}
            </div>

            {/* Net Flow Card */}
            <div className="summary-card card-slide-right">
              <div className="summary-card-label">{t('liquidationSummary.netFlow')}</div>
              <div className={`summary-card-value ${animatedValues.netFlow >= 0 ? 'text-success' : 'text-danger'}`}>
                {animatedValues.netFlow >= 0 ? '+' : ''}{formatCurrency(animatedValues.netFlow)} USD
              </div>
              <div className="summary-card-sublabel">
                {t('liquidationSummary.longLiq')}: {formatCurrency(transformedSummary.longLiqVolume)} • {t('liquidationSummary.shortLiq')}: {formatCurrency(transformedSummary.shortLiqVolume)}
              </div>
            </div>
          </div>
        ) : (
          <div className="liquidation-summary-error">
            <p>{t('liquidationSummary.noData')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LiquidationSummaryModal;
